// @flow
import { number, object } from 'yup';

import { MAX_INPUT_VALUE_FOR_ANY_FIELD } from '../constants';

/**
 * Takes the schema and returns a new schema that considers an
 * object to be valid if it is empty or if it conforms to the schema
 *
 * @param {object} schema - The schema to validate the object if it is not empty
 */
export const emptyOrValidObject = (schema) =>
  object().test(
    'is-empty-object-or-valid',
    'Object should either be empty or valid',
    (value, { path, createError }) => {
      /**
       * If an empty object is passed or property itself is missing, then it is valid
       */
      if (Object.keys(value ?? {}).length === 0) {
        return true;
      }

      try {
        /**
         * If value does not conform to the schema, An error will be thrown.
         * Otherwise, true is returned indicating that the data is valid
         */
        object(schema).validateSync(value);
        return true;
      } catch (err) {
        return createError({
          message: err.message,
          // Prefix the parent path with the current path
          path: `${path}.${err.path}`,
        });
      }
    }
  );

/**
 * Base schema for a non-negative numeric input bounded by the global max, with the standard
 * "Required Field" messaging used across the cluster/estimate forms.
 *
 * Layer field-specific rules on top via normal yup chaining,
 * e.g. `.required()`, `.integer()`, `.test(...)`, or `.max(otherLimit)`.
 */
export const getNonNegativeNumberSchema = () =>
  number()
    .min(0)
    .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
    .label('This field')
    .typeError('Required Field');

/**
 * Same as getNonNegativeNumberSchema, but bounded to whole numbers - for count-like inputs
 * (e.g. CCU count, token counts) rather than free-form numeric ones.
 */
export const getNonNegativeIntegerSchema = () => getNonNegativeNumberSchema().integer();
